import React, { useRef, useState } from 'react';

import { AlineTransforms, createDocumentRoute, Select } from '@aline/common';
import type { CardProps } from '@mui/material';
import { Card, CardHeader, IconButton, useTheme } from '@mui/material';
import copy from 'copy-to-clipboard';
import { AlineAvatar, AlineTooltip } from 'src/components';
import { PrivateCommentBadge } from 'src/components/comments/PrivateCommentBadge';
import { LinkIcon } from 'src/components/icons/LinkIcon';
import { VertDotsIcon } from 'src/components/icons/VertDotsIcon';
import { isDev } from 'src/config';
import { useEditorTabStateSelector } from 'src/editor/editor-tab-state-registry';
import { useSelectCurrentUser } from 'src/gql';
import {
  domElIdCreators,
  getEntityDisplayDetails,
  useDispatchSuccessSnack,
  useDocumentParams,
} from 'src/helpers';
import { activeCommentIdVar, useActiveCommentId } from 'src/hooks';
import { getIsUserInDocOwnerOrg, useGetCurrentDoc } from 'src/hooks/document';
import { ReactEditor, useMaybeEditor } from 'src/layout-engine';
import { useEditingMode } from 'src/store/document/selectors';
import { makeStyles } from 'tss-react/mui';

import { CommentBoxContent } from './CommentBoxContent';
import { CommentBoxMenu } from './CommentBoxMenu';
import type { CommentPanelComment } from './CommentsDrawer';
import { CommentSubHeader } from './CommentSubHeader';

const useStyles = makeStyles<StyleProps>()((theme, { commentActive, showPointer }) => ({
  card: {
    // Provide a transparent border in the non-active case so content inside the box doesn't jump when the border becomes active.
    border: commentActive ? `1px solid ${theme.palette.primary.main}` : '1px solid transparent',
    boxSizing: 'border-box',
    cursor: showPointer ? 'pointer' : undefined,
    margin: theme.spacing(1),
  },
  cardAction: {
    alignItems: 'center',
    alignSelf: 'center',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(0.5),
    margin: 0,
  },
  cardHeader: {
    padding: theme.spacing(1),
  },
  cardHeaderContent: {
    /**
     * By default flex containers don't shrink below their content width.  So, to make this container work with no-wrap text, we set minWidth to 0
     * so the container resizes correctly.
     */
    minWidth: 0,
  },
  vertDotsColor: {
    color: theme.appColors.iconsType,
  },
}));

interface StyleProps {
  commentActive: boolean;
  showPointer: boolean;
}

interface CommentProps extends CardProps {
  commentOrReply: CommentPanelComment;
  isRootCommentResolved: boolean;
  rootCommentId: string;
  showReplies?: boolean;
  /** Thread privacy from the root comment. Replies inherit this; do not read reply.isPrivate. */
  threadIsPrivate?: boolean;
}

export function getIsRootComment(comment: CommentPanelComment): boolean {
  return comment.parent === null;
}

export function CommentBox(props: CommentProps): JSX.Element {
  const {
    commentOrReply,
    isRootCommentResolved,
    rootCommentId,
    showReplies = false,
    threadIsPrivate = Boolean(commentOrReply.isPrivate),
  } = props;

  const theme = useTheme();
  const cardRef = useRef(null);
  const editor = useMaybeEditor();
  const { viewing } = useEditingMode();
  const { documentId } = useDocumentParams();
  const currentUser = useSelectCurrentUser();
  const { document } = useGetCurrentDoc();
  const canViewPrivateBadge = getIsUserInDocOwnerOrg(currentUser, document);
  const activeCommentId = useActiveCommentId();
  const [editing, setEditing] = useState<boolean>(false);
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [mouseOver, setMouseOver] = useState<boolean>(false);
  const dispatchCopyLinkSuccess = useDispatchSuccessSnack({
    message: 'Comment link copied to clipboard.',
  });

  const activePanel = useEditorTabStateSelector(state => state.comments.activePanel);

  const isRootComment = getIsRootComment(commentOrReply);
  const commentActive =
    activePanel !== 'ViewThread' && isRootComment && activeCommentId === commentOrReply.nodeRefId;
  const showPointer = isRootComment && activePanel !== 'ViewThread';
  const { classes } = useStyles({ commentActive, showPointer });

  function copyCommentLinkToClipboard(e: React.MouseEvent): void {
    e.stopPropagation();

    const pathToComment = commentOrReply.nodeRefId
      ? createDocumentRoute(documentId, { commentId: commentOrReply.nodeRefId })
      : null;
    if (!pathToComment) return;

    copy(`${window.location.origin}${pathToComment}`, { debug: isDev });
    dispatchCopyLinkSuccess();
  }

  function getAuthorDetails(): { authorLabel: string; backgroundColor?: string } {
    if (!commentOrReply.author) {
      if (commentOrReply.importedAuthorName) {
        return {
          authorLabel: `${commentOrReply.importedAuthorName}`,
          backgroundColor: theme.appColors.accent2,
        };
      }
      return { authorLabel: 'Anonymous', backgroundColor: theme.appColors.accent2 };
    }

    const { avatarColor, primary: authorLabel } = getEntityDisplayDetails(commentOrReply.author);

    return { authorLabel, backgroundColor: avatarColor };
  }

  const { authorLabel, backgroundColor } = getAuthorDetails();
  const avatarInfo = commentOrReply.author
    ? commentOrReply.author
    : commentOrReply.importedAuthorName
      ? commentOrReply.importedAuthorName.charAt(0).toUpperCase()
      : null;

  return (
    <>
      <Card
        ref={cardRef}
        className={classes.card}
        data-cy={Select.document.editor.rightDrawer.comments.commentBoxCard.container}
        /** Replies don't have a nodeId property... */
        id={domElIdCreators.docCommentBoxEl(commentOrReply.nodeRefId ?? commentOrReply.id)}
        raised={mouseOver && !commentActive}
        onClick={() => {
          if (!editor || !isRootComment || !commentOrReply.nodeRefId) return;

          AlineTransforms.selectComment(editor, {
            commentId: commentOrReply.nodeRefId,
            select: isRootCommentResolved ? 'entire-comment' : 'start',
          });
          ReactEditor.blur(editor);

          /**
           * When selecting a resolved comment, we want to clear out activeCommentId since resolved comments can't be active.
           * Use Promise.resolve to activeCommentId gets set to null *after* editor.onChange.
           */
          if (isRootCommentResolved) Promise.resolve().then(() => activeCommentIdVar(null));
        }}
        onMouseEnter={(): void => {
          if (editing || !isRootComment || activePanel === 'ViewThread') return;

          setMouseOver(true);
        }}
        onMouseLeave={(): void => {
          if (editing || !isRootComment || activePanel === 'ViewThread') return;

          setMouseOver(false);
        }}
      >
        <CardHeader
          action={
            <>
              {canViewPrivateBadge && threadIsPrivate && <PrivateCommentBadge />}
              {isRootComment && (
                <AlineTooltip arrow placement="bottom" title="Copy comment link">
                  <div>
                    <IconButton size="small" onClick={copyCommentLinkToClipboard}>
                      <LinkIcon />
                    </IconButton>
                  </div>
                </AlineTooltip>
              )}
              {(!threadIsPrivate || canViewPrivateBadge) && (
                <AlineTooltip arrow placement="bottom" title="Options">
                  <div>
                    <IconButton
                      aria-label="Options"
                      classes={{ colorPrimary: classes.vertDotsColor }}
                      color="primary"
                      disabled={viewing || editing}
                      size="small"
                      onClick={(event: React.MouseEvent): void => {
                        event.stopPropagation();
                        setMenuOpen(!menuOpen);
                      }}
                    >
                      <VertDotsIcon />
                    </IconButton>
                  </div>
                </AlineTooltip>
              )}
            </>
          }
          avatar={<AlineAvatar avatarInfo={avatarInfo} styles={{ backgroundColor }} />}
          classes={{
            action: classes.cardAction,
            content: classes.cardHeaderContent,
            root: classes.cardHeader,
          }}
          subheader={
            <CommentSubHeader
              comment={commentOrReply}
              isRootCommentResolved={isRootCommentResolved}
            />
          }
          subheaderTypographyProps={{ variant: 'caption' }}
          title={authorLabel}
          titleTypographyProps={{ noWrap: true, variant: 'caption' }}
        />
        <CommentBoxContent
          commentOrReply={commentOrReply}
          editing={editing}
          isComment={isRootComment}
          rootCommentId={rootCommentId}
          setEditing={setEditing}
          showReplies={showReplies}
          threadIsPrivate={threadIsPrivate}
        />
      </Card>
      <CommentBoxMenu
        anchorEl={cardRef.current}
        commentOrReply={commentOrReply}
        isComment={isRootComment}
        isRootCommentResolved={isRootCommentResolved}
        open={menuOpen}
        rootCommentId={rootCommentId}
        setEditing={setEditing}
        setMenuOpen={setMenuOpen}
        threadIsPrivate={threadIsPrivate}
      />
    </>
  );
}
